import React from 'react';
import { Box, Typography, Link, Divider, Tooltip, IconButton, useTheme } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getMetricDefinition, getRagColor, getStrainBandColors } from '../../utils';

interface MetricInfoTooltipContentProps {
  /** metricId (preferred) or display label used to resolve the definition. */
  metricKey: string;
  /** Optional fallback definition text when no entry exists for the metric. */
  fallbackText?: string;
  /** Optional trailing note, e.g. how the trend % is calculated. */
  trendNote?: React.ReactNode;
}

/**
 * Shared tooltip body: plain-English definition → general ranges (colour-keyed)
 * → cited source → optional trend note. Used by both StatCard and the Wellness
 * Overview gauges so the "ℹ️" content is identical everywhere.
 */
export const MetricInfoTooltipContent: React.FC<MetricInfoTooltipContentProps> = ({
  metricKey,
  fallbackText,
  trendNote,
}) => {
  const theme = useTheme();
  const definition = getMetricDefinition(metricKey);

  return (
    <Box sx={{ p: 0.5, maxWidth: 280 }}>
      <Typography variant='body2' sx={{ fontWeight: 600, mb: 0.5 }}>
        What is {definition?.plainName ?? metricKey}?
      </Typography>
      <Typography variant='body2'>
        {definition?.definition ??
          fallbackText ??
          `The average ${metricKey.toLowerCase()} across the selected team(s) over the chosen period.`}
      </Typography>

      {definition?.bands &&
        definition.bands.length > 0 &&
        (() => {
          const isStrain = metricKey === 'strain' || metricKey === 'day_strain';
          const strainColors = isStrain ? getStrainBandColors(theme) : null;
          return (
            <>
              <Typography variant='body2' sx={{ fontWeight: 600, mt: 1, mb: 0.5 }}>
                General ranges
              </Typography>
              {definition.bands.map((band, index) => (
                <Box
                  key={`${band.label}-${band.range}`}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      flexShrink: 0,
                      backgroundColor: band.ragColor
                        ? getRagColor(band.ragColor, theme)
                        : strainColors
                          ? strainColors[index]
                          : theme.palette.text.disabled,
                    }}
                  />
                  <Typography variant='body2' sx={{ flex: 1 }}>
                    <strong>{band.range}</strong> — {band.label}
                  </Typography>
                </Box>
              ))}
            </>
          );
        })()}

      {definition?.source && (
        <Typography variant='caption' component='div' sx={{ mt: 0.75, color: 'inherit', opacity: 0.8 }}>
          Source:{' '}
          {definition.source.url ? (
            <Link
              href={definition.source.url}
              target='_blank'
              rel='noopener noreferrer'
              sx={{ color: 'inherit', textDecorationColor: 'inherit' }}
            >
              {definition.source.label}
            </Link>
          ) : (
            definition.source.label
          )}
        </Typography>
      )}

      {trendNote && (
        <>
          <Divider sx={{ my: 0.75, borderColor: 'rgba(255,255,255,0.2)' }} />
          <Typography variant='caption' component='div' sx={{ opacity: 0.8 }}>
            {trendNote}
          </Typography>
        </>
      )}
    </Box>
  );
};

interface MetricInfoIconProps extends MetricInfoTooltipContentProps {
  /** Icon font size; defaults to a small inline icon. */
  fontSize?: number;
}

/** Small ℹ️ icon button that shows the shared metric-info tooltip on hover. */
export const MetricInfoIcon: React.FC<MetricInfoIconProps> = ({ fontSize = 0.85, ...contentProps }) => (
  <Tooltip title={<MetricInfoTooltipContent {...contentProps} />} arrow placement='top'>
    <IconButton
      size='small'
      sx={{
        p: 0,
        color: 'text.disabled',
        '&:hover': { color: 'text.secondary' },
      }}
      aria-label='Metric information'
    >
      <InfoOutlinedIcon sx={{ fontSize: `${fontSize}rem` }} />
    </IconButton>
  </Tooltip>
);
